import { type ReactNode, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

type SortDir = 'asc' | 'desc';
type Col = { key: string; label: string };
type OnSort = (k: string, d: SortDir) => void;

function buildCols(
  creatorLabel: string,
  followersLabel: string,
  isAdmin: boolean
): Col[] {
  const cols: Col[] = [
    { key: 'ig_username', label: creatorLabel },
    { key: 'followers', label: followersLabel },
    { key: 'category', label: 'Categoría' },
    { key: 'rank', label: 'Rango' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Fecha' },
  ];
  if (isAdmin) cols.push({ key: '', label: 'Collector' });
  cols.push({ key: '', label: 'Post' });
  return cols;
}

const TH = cn(
  'px-md py-sm text-[11px] font-medium uppercase tracking-wider',
  'cursor-pointer select-none transition-colors'
);

function SortIcon({ dir }: { dir: SortDir }): ReactNode {
  const I = dir === 'asc' ? ChevronUp : ChevronDown;
  return <I size={11} className="text-brand-red" />;
}

function nextDir(cur: SortDir, active: boolean): SortDir {
  return !active ? 'desc' : cur === 'desc' ? 'asc' : 'desc';
}

function thClass(active: boolean, sortable: boolean): string {
  return cn(
    TH,
    sortable && 'cursor-pointer',
    active
      ? 'text-text-secondary'
      : 'text-text-tertiary/50 hover:text-text-tertiary'
  );
}

type ThProps = {
  col: Col;
  active: boolean;
  sortDir: SortDir;
  onSort: OnSort;
};

function HeaderTh({ col, active, sortDir, onSort }: ThProps): ReactNode {
  const sortable = col.key !== '';
  const onClick = sortable
    ? () => onSort(col.key, nextDir(sortDir, active))
    : undefined;
  return (
    <th onClick={onClick} className={thClass(active, sortable)}>
      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
        {col.label}
        {active && <SortIcon dir={sortDir} />}
      </span>
    </th>
  );
}

type Props = {
  sortKey: string;
  sortDir: SortDir;
  onSort: OnSort;
  creatorLabel: string;
  followersLabel: string;
  isAdmin: boolean;
};

function HeaderRow(p: Props & { cols: Col[] }): ReactNode {
  return (
    <tr className="border-b border-white/[0.06]">
      {p.cols.map((col, i) => (
        <HeaderTh
          key={col.key || `ns-${i}`}
          col={col}
          active={p.sortKey === col.key}
          sortDir={p.sortDir}
          onSort={p.onSort}
        />
      ))}
    </tr>
  );
}

export function SortableHeader(p: Props): ReactNode {
  const cols = useMemo(
    () => buildCols(p.creatorLabel, p.followersLabel, p.isAdmin),
    [p.creatorLabel, p.followersLabel, p.isAdmin]
  );
  return (
    <thead>
      <HeaderRow {...p} cols={cols} />
    </thead>
  );
}
